import React, { useState, useEffect } from 'react';
import styles from './accordionItem.module.scss';
import clsx from 'clsx';
import CircleProgress from '../../../_common/progress/circle/circleProgress';
import { SPROUTIcon } from 'sprout-mdbreact';

export interface AccordionItemProps {
  active?: boolean;
  className?: string;
  disabled?: boolean;
  header: React.ReactNode;
  loading?: boolean;
  onExpand: () => void;
  headerClassName?: string;
  bodyClassName?: string;
}

/**
 * Internal Component to render the header section
 */
// TODO: Style for disabled state
const AccordionItemHeader: React.FC<{
  active: boolean;
  disabled?: boolean;
  loading?: boolean;
  onExpand: () => void;
  className?: string;
}> = props => {
  return (
    <div
      className={clsx(
        styles.header,
        {
          // Toggle loading styles
          [styles.headerLoading]: props.loading,
          // Enable hover effect when component is not loading and not disabled
          [styles.headerIdle]: !props.loading && !props.disabled,
        },
        props.className
      )}
      // Pass onExpand Fn when component is not loading and not disabled
      onClick={!props.loading && !props.disabled ? props.onExpand : null}
    >
      {/* Toggle loading state */}
      {props.loading ? (
        <div className={styles.loader}>
          <CircleProgress height={25} width={25} />
        </div>
      ) : (
        <React.Fragment>
          <div className={styles.headerContent}>{props.children}</div>
          <SPROUTIcon
            icon="caret-down-regular-1"
            className={clsx(styles.headerControl, {
              [styles.invertControl]: props.active,
            })}
          />
        </React.Fragment>
      )}
    </div>
  );
};

/**
 * Internal Component to render the body section
 */
const AccordionItemBody: React.FC<{ active: boolean; className?: string }> = props => {
  return (
    <div
      className={clsx(styles.body, {
        // Toggle body height transition
        [styles.open]: props.active,
      })}
    >
      <div className={clsx(styles.inner, props.className)}>{props.children}</div>
    </div>
  );
};

/**
 * Component to render a collapsible content
 * @param active - Toggle to expand the content
 * @param header - Content to be displayed on the header
 * @param onExpand - Function to expand the component. This will be bound to the caret button.
 */
const AccordionItem: React.FC<AccordionItemProps &
  React.HTMLAttributes<HTMLDivElement>> = ({
  active,
  className,
  children,
  disabled,
  header,
  loading,
  onExpand,
  headerClassName,
  bodyClassName,
  ...otherProps
}) => {
  const [toggleBackground, toggleBackgroundState] = useState<boolean>(false);

  useEffect(() => {
    // Turn on animation if item is set to active
    if (active && !toggleBackground) {
      toggleBackgroundState(true);
    }
    // Turn off animation if item is set to inactive
    if (!active && toggleBackground) {
      setTimeout(() => toggleBackgroundState(false), 410);
    }
  }, [active, toggleBackground]);

  return (
    <div
      className={clsx(
        styles.container,
        { [styles.active]: active, [styles.bg]: toggleBackground },
        className
      )}
      {...otherProps}
    >
      <AccordionItemHeader
        active={active}
        disabled={disabled}
        loading={loading}
        onExpand={onExpand}
        className={headerClassName}
      >
        {header}
      </AccordionItemHeader>
      <AccordionItemBody active={active} className={bodyClassName}>
        {children}
      </AccordionItemBody>
    </div>
  );
};

export default AccordionItem;
